<script>
// App-bar status for the offline queues: a chip counting the offline changes
// waiting to sync (red once any of them failed or conflicted), plus a wifi-off
// chip while the browser is offline. Clicking it opens the one location this
// browser holds work or a download for, in its section; with several, it opens
// the Offline list, where the work syncs or gets inspected per item.
//
// Recounts are event-driven (the queues ping offline-events on every stored
// change). No polling.
import { allItems as outboxItems } from '@src/graphql/outbox'
import { allUploads } from '@src/graphql/upload-queue'
import { subscribeOfflineQueues } from '@src/graphql/offline-events'
import { reportError } from '@utils/report-error'

export default {
  name: 'OfflinePendingChip',
  data() {
    return {
      pending: 0,
      failed: 0,
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      unsubscribe: null,
    }
  },
  mounted() {
    this.recount()
    this.unsubscribe = subscribeOfflineQueues(this.recount)
    window.addEventListener('online', this.onConnectivity)
    window.addEventListener('offline', this.onConnectivity)
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe()
    window.removeEventListener('online', this.onConnectivity)
    window.removeEventListener('offline', this.onConnectivity)
  },
  methods: {
    onConnectivity() {
      this.online = navigator.onLine
    },
    async recount() {
      try {
        const [items, uploads] = await Promise.all([
          outboxItems(),
          allUploads(),
        ])
        this.pending = items.length + uploads.length
        this.failed =
          items.filter(
            (item) => item.status === 'failed' || item.status === 'conflict'
          ).length + uploads.filter((job) => job.status === 'failed').length
      } catch (err) {
        reportError(err, { source: 'offline-pending-chip' })
      }
    },
    async open() {
      const target = await this.offlineTarget().catch(() => null)
      this.$router
        .push(target || { path: '/infrared', query: { tab: 'offline' } })
        .catch(() => {})
    },
    // The single location the queued work belongs to (Infrared, or LOTO for
    // the people LOTO admits), as the devices page takes it, opened on that
    // section's Offline tab. Null when the work spans several locations: the
    // Offline list then lists them all.
    //
    // THE WORK DECIDES, NOT THE DOWNLOADS. The chip counts queued changes, and
    // a click used to land on whichever location held a download. Those are
    // not always the same place: work saved under the wrong site's download,
    // or work whose download is gone, sent the tech to a page that showed
    // none of the changes the chip was counting. Only with no work tied to
    // a location do the downloads decide, as before.
    async offlineTarget() {
      // Loaded when clicked, not with the app bar: the offline stores are big and
      // only this click needs them.
      const [infraredStore, lotoStore] = await Promise.all([
        import('@src/graphql/infrared-offline'),
        import('@src/graphql/loto-offline'),
      ])
      // LOTO's offline work is for those who may work LOTO offline.
      const can = this.$store && this.$store.getters['auth/can']
      const lotoOffline = Boolean(can && can('loto.offline'))
      const [infrared, loto, items, uploads] = await Promise.all([
        infraredStore.listOfflineLocations(),
        lotoOffline ? lotoStore.listOfflineLocations() : [],
        outboxItems(),
        allUploads(),
      ])
      // One place per location (ids compared as strings, see
      // outbox.matchesLocation), Infrared preferred when both features hold it.
      const add = (places, locationId, section) => {
        const key = String(locationId)
        const known = places.get(key)
        if (!known || (known.section === 'loto' && section === 'infrared')) {
          places.set(key, { locationId, section })
        }
      }
      let places = new Map()
      for (const entry of [...items, ...uploads]) {
        if (entry.locationId == null) continue
        // Untagged work is Infrared's (see outbox.matchesFeature).
        const section = entry.feature === 'loto' ? 'loto' : 'infrared'
        if (section === 'loto' && !lotoOffline) continue
        add(places, entry.locationId, section)
      }
      if (!places.size) {
        places = new Map()
        for (const record of infrared)
          add(places, record.locationId, 'infrared')
        for (const record of loto) add(places, record.locationId, 'loto')
      }
      if (places.size !== 1) return null
      const [place] = places.values()
      const key = String(place.locationId)
      const record = [...infrared, ...loto].find(
        (r) => String(r.locationId) === key && r.name
      )
      return {
        name: 'devices',
        query: {
          locationId: key,
          locationName: record ? record.name : '',
          section: place.section,
          offline: place.section,
        },
      }
    },
  },
}
</script>

<template>
  <div class="d-flex align-center">
    <v-chip
      v-if="!online"
      color="white"
      variant="outlined"
      size="small"
      class="mr-2 font-weight-medium"
    >
      {{ $t('infrared.offline.indicatorOffline') }}
    </v-chip>
    <v-tooltip
      v-if="pending"
      :text="$t('infrared.offline.indicatorPending', { count: pending })"
      location="bottom"
    >
      <template v-slot:activator="{ props }">
        <v-chip
          v-bind="props"
          :color="failed ? 'yellow-lighten-3' : 'white'"
          variant="outlined"
          size="small"
          class="mr-2 font-weight-medium"
          prepend-icon="$download"
          @click="open"
        >
          {{ pending }}
        </v-chip>
      </template>
    </v-tooltip>
  </div>
</template>
